// Story 5.6 — "My listings" management surface at /account/marketplace/listings.
//
// Closes the seller-side reversal path of Epic 5. Owner-filtered view of
// the caller's own share + sealed-dossier listings. Active listings
// expose a destructive "Withdraw listing" Button (per UX-DR L1754); the
// confirm modal mounts at page level (controlled by `cancelTarget`
// state) mirroring the Story 5.5 MarketplacePage ↔ BuyListingConfirmModal
// pattern.

import { Button, formatIssueNumber } from "@greenhold/components";
import { useState } from "react";

import { EmptyState } from "../components/EmptyState";
import { CancelListingConfirmDialog } from "../features/marketplace/CancelListingConfirmDialog";
import {
  EditSealedDossierListingDialog,
  EditShareListingDialog,
} from "../features/marketplace/EditListingDialog";
import type { ShareTier } from "../features/marketplace/useShareTierFloorPrice";
import {
  IncomingOffersPanel,
  MyOffersSection,
} from "../features/marketplace/OfferManagementPanels";
import {
  useMyShareListings,
  type MyListingStatus,
  type MyShareListingRow,
} from "../features/marketplace/useMyShareListings";
import {
  useMySealedDossierListings,
  type MySealedDossierListingRow,
} from "../features/marketplace/useMySealedDossierListings";

const STATUS_OPTIONS: { label: string; value: MyListingStatus | "all" }[] = [
  { label: "All statuses", value: "all" },
  { label: "Active", value: "active" },
  { label: "Sold", value: "sold" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Expired", value: "expired" },
];

const DEFAULT_PER_PAGE = 24;

function formatEuros(cents: number): string {
  return `€${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      data-testid={`status-badge-${status}`}
      className="inline-flex items-center rounded border border-brand-paper-300 bg-brand-paper-200 px-2 py-0.5 font-mono text-xs uppercase tracking-wider text-brand-forest-900/75"
    >
      {status}
    </span>
  );
}

interface ShareListingRowDisplayProps {
  row: MyShareListingRow;
  onWithdraw: (listingId: string) => void;
  onEdit: (row: MyShareListingRow) => void;
}

function ShareListingRowDisplay({
  row,
  onWithdraw,
  onEdit,
}: ShareListingRowDisplayProps) {
  return (
    <li
      data-testid={`my-share-row-${row.listing_id}`}
      className="border-b border-brand-paper-200 last:border-b-0"
    >
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {row.share.creator_display_name}
          </p>
          <p className="text-xs">
            {row.share.tier.toUpperCase()} · Issue{" "}
            {formatIssueNumber(row.share.edition_number)} · Serial #
            {row.share.share_serial}
          </p>
          <p className="text-xs">
            <span className="opacity-60">Listed</span> {formatDate(row.listed_at)}
          </p>
        </div>
        <div className="font-mono text-sm font-semibold">{formatEuros(row.price_cents)}</div>
        <StatusBadge status={row.status} />
        <div className="flex w-40 flex-col items-end gap-1.5">
          {row.status === "active" && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onEdit(row)}
                data-testid={`edit-share-${row.listing_id}`}
              >
                Edit listing
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onWithdraw(row.listing_id)}
              >
                Withdraw listing
              </Button>
            </>
          )}
        </div>
      </div>
      {row.status === "active" && row.allow_bidding && (
        <div className="px-3 pb-3">
          <IncomingOffersPanel kind="share" listingId={row.listing_id} />
        </div>
      )}
    </li>
  );
}

interface SealedDossierListingRowDisplayProps {
  row: MySealedDossierListingRow;
  onWithdraw: (listingId: string) => void;
  onEdit: (row: MySealedDossierListingRow) => void;
}

function SealedDossierListingRowDisplay({
  row,
  onWithdraw,
  onEdit,
}: SealedDossierListingRowDisplayProps) {
  return (
    <li
      data-testid={`my-sealed-dossier-row-${row.listing_id}`}
      className="border-b border-brand-paper-200 last:border-b-0"
    >
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {row.sealed_dossier.portfolio_title}
          </p>
          <p className="text-xs">
            <span className="opacity-60">Sealed dossier · Issue</span>{" "}
            {formatIssueNumber(row.sealed_dossier.edition_number)}
          </p>
          <p className="text-xs">
            <span className="opacity-60">Listed</span> {formatDate(row.listed_at)}
          </p>
        </div>
        <div className="font-mono text-sm font-semibold">{formatEuros(row.price_cents)}</div>
        <StatusBadge status={row.status} />
        <div className="flex w-40 flex-col items-end gap-1.5">
          {row.status === "active" && (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onEdit(row)}
                data-testid={`edit-sealed-dossier-${row.listing_id}`}
              >
                Edit listing
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onWithdraw(row.listing_id)}
              >
                Withdraw listing
              </Button>
            </>
          )}
        </div>
      </div>
      {row.status === "active" && row.allow_bidding && (
        <div className="px-3 pb-3">
          <IncomingOffersPanel kind="sealed_dossier" listingId={row.listing_id} />
        </div>
      )}
    </li>
  );
}

interface CancelTarget {
  kind: "share" | "sealed_dossier";
  listingId: string;
}

type EditTarget =
  | {
      kind: "share";
      listingId: string;
      priceCents: number;
      allowBidding: boolean;
      creatorUserId: string;
      tier: ShareTier;
    }
  | {
      kind: "sealed_dossier";
      listingId: string;
      priceCents: number;
      allowBidding: boolean;
      dropId: string;
    };

export function MyListingsPage() {
  const [shareStatusFilter, setShareStatusFilter] = useState<
    MyListingStatus | "all"
  >("all");
  const [shareSealedDossierStatusFilter, setSealedDossierStatusFilter] =
    useState<MyListingStatus | "all">("all");
  const [cancelTarget, setCancelTarget] = useState<CancelTarget | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  const shareListings = useMyShareListings({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    status: shareStatusFilter === "all" ? undefined : shareStatusFilter,
  });
  const sealedDossierListings = useMySealedDossierListings({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    status:
      shareSealedDossierStatusFilter === "all"
        ? undefined
        : shareSealedDossierStatusFilter,
  });

  function handleWithdrawShare(listingId: string) {
    setCancelTarget({ kind: "share", listingId });
  }

  function handleWithdrawSealedDossier(listingId: string) {
    setCancelTarget({ kind: "sealed_dossier", listingId });
  }

  function handleEditShare(row: MyShareListingRow) {
    setEditTarget({
      kind: "share",
      listingId: row.listing_id,
      priceCents: row.price_cents,
      allowBidding: row.allow_bidding,
      creatorUserId: row.share.creator_user_id,
      tier: row.share.tier as ShareTier,
    });
  }

  function handleEditSealedDossier(row: MySealedDossierListingRow) {
    setEditTarget({
      kind: "sealed_dossier",
      listingId: row.listing_id,
      priceCents: row.price_cents,
      allowBidding: row.allow_bidding,
      dropId: row.sealed_dossier.drop_id,
    });
  }

  return (
    // Light paper page per the design (MarketPage.dc.html market idiom).
    <div className="min-h-[calc(100vh-3.5rem)] w-full bg-brand-paper-200 text-brand-forest-900">
    <main className="mx-auto flex w-full max-w-4xl flex-col gap-[var(--gh-rhythm-consumer,24px)] p-6">
      <header className="space-y-1">
        <h1 className="font-heading text-3xl font-bold">My listings</h1>
        <p className="font-body text-sm opacity-70">
          Manage your active marketplace listings or browse your history.
        </p>
      </header>

      {/* ---- Share listings section ---- */}
      <section data-testid="my-share-listings-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold">Certificate listings</h2>
          <label className="text-sm flex items-center gap-2">
            <span className="opacity-70">Filter:</span>
            <select
              data-testid="share-status-filter"
              value={shareStatusFilter}
              onChange={(e) =>
                setShareStatusFilter(
                  e.target.value as MyListingStatus | "all",
                )
              }
              className="rounded border border-brand-paper-300 bg-brand-paper-50 px-2 py-1 font-mono text-xs text-brand-forest-900 focus:outline-none focus:ring-2 focus:ring-brand-forest-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {shareListings.isLoading && (
          <p className="text-sm opacity-60">Loading…</p>
        )}
        {shareListings.isError && (
          <p className="text-sm text-state-error-fill">
            Failed to load your share listings.
          </p>
        )}
        {shareListings.data?.data.length === 0 && (
          <div data-testid="share-listings-empty">
            <EmptyState
              aria-label="No certificate listings"
              description="No certificate listings to show."
            />
          </div>
        )}
        {shareListings.data && shareListings.data.data.length > 0 && (
          <ul className="rounded-lg border border-brand-paper-300 bg-brand-paper-50">
            {shareListings.data.data.map((row) => (
              <ShareListingRowDisplay
                key={row.listing_id}
                row={row}
                onWithdraw={handleWithdrawShare}
                onEdit={handleEditShare}
              />
            ))}
          </ul>
        )}
      </section>

      {/* ---- Sealed-dossier listings section ---- */}
      <section data-testid="my-sealed-dossier-listings-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold">Sealed dossier listings</h2>
          <label className="text-sm flex items-center gap-2">
            <span className="opacity-70">Filter:</span>
            <select
              data-testid="sealed-dossier-status-filter"
              value={shareSealedDossierStatusFilter}
              onChange={(e) =>
                setSealedDossierStatusFilter(
                  e.target.value as MyListingStatus | "all",
                )
              }
              className="rounded border border-brand-paper-300 bg-brand-paper-50 px-2 py-1 font-mono text-xs text-brand-forest-900 focus:outline-none focus:ring-2 focus:ring-brand-forest-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {sealedDossierListings.isLoading && (
          <p className="text-sm opacity-60">Loading…</p>
        )}
        {sealedDossierListings.isError && (
          <p className="text-sm text-state-error-fill">
            Failed to load your sealed-dossier listings.
          </p>
        )}
        {sealedDossierListings.data?.data.length === 0 && (
          <div data-testid="sealed-dossier-listings-empty">
            <EmptyState
              aria-label="No sealed-dossier listings"
              description="No sealed-dossier listings to show."
            />
          </div>
        )}
        {sealedDossierListings.data &&
          sealedDossierListings.data.data.length > 0 && (
            <ul className="rounded-lg border border-brand-paper-300 bg-brand-paper-50">
              {sealedDossierListings.data.data.map((row) => (
                <SealedDossierListingRowDisplay
                  key={row.listing_id}
                  row={row}
                  onWithdraw={handleWithdrawSealedDossier}
                  onEdit={handleEditSealedDossier}
                />
              ))}
            </ul>
          )}
      </section>

      {/* ---- My offers (buyer-facing) ---- */}
      <MyOffersSection />

      {/* ---- Cancel confirm dialog mount ---- */}
      {cancelTarget && (
        <CancelListingConfirmDialog
          kind={cancelTarget.kind}
          listingId={cancelTarget.listingId}
          open={cancelTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setCancelTarget(null);
            }
          }}
        />
      )}

      {/* ---- Edit listing dialog mount ---- */}
      {editTarget?.kind === "share" && (
        <EditShareListingDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          listingId={editTarget.listingId}
          creatorUserId={editTarget.creatorUserId}
          tier={editTarget.tier}
          priceCents={editTarget.priceCents}
          allowBidding={editTarget.allowBidding}
        />
      )}
      {editTarget?.kind === "sealed_dossier" && (
        <EditSealedDossierListingDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditTarget(null);
          }}
          listingId={editTarget.listingId}
          dropId={editTarget.dropId}
          priceCents={editTarget.priceCents}
          allowBidding={editTarget.allowBidding}
        />
      )}
    </main>
    </div>
  );
}
